import { useState } from 'react';
import { API_BASE_URL } from './config';

export default function AdminForcePasswordReset({ user, onResetComplete }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    return '';
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate inputs
    const newPasswordError = validatePassword(newPassword);
    if (newPasswordError) {
      setError(newPasswordError);
      return;
    }

    if (!confirmPassword) {
      setError('Please confirm your password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          // For forced password reset, currentPassword is optional
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to reset password');
        return;
      }

      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');

      // Redirect after 2 seconds
      setTimeout(() => {
        if (onResetComplete) {
          onResetComplete();
        } else {
          window.location.href = '/admin';
        }
      }, 2000);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleReset} style={{ marginTop: '30px' }}>
      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '15px',
          backgroundColor: 'var(--color-primary-soft)',
          border: '1px solid var(--color-primary-soft)',
          borderRadius: '4px',
          color: 'var(--color-primary)',
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px',
          marginBottom: '15px',
          backgroundColor: 'var(--color-secondary-soft)',
          border: '1px solid var(--color-secondary-soft)',
          borderRadius: '4px',
          color: 'var(--color-secondary)',
        }}>
          Password reset successfully! Redirecting to admin dashboard...
        </div>
      )}

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px' }}>
          New Password
        </label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter new password (minimum 8 characters)"
          disabled={loading}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid var(--border-strong)',
            borderRadius: '4px',
            fontSize: '14px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px' }}>
          Confirm Password
        </label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          disabled={loading}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid var(--border-strong)',
            borderRadius: '4px',
            fontSize: '14px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: loading ? 'var(--border-strong)' : 'var(--color-secondary)',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '16px',
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? 'Resetting Password...' : 'Reset Password'}
      </button>

      <p style={{ marginTop: '20px', fontSize: '14px', color: 'var(--text-secondary)' }}>
        <strong>Password Requirements:</strong>
        <ul>
          <li>Minimum 8 characters</li>
          <li>Mix of uppercase, lowercase, numbers, and special characters recommended</li>
        </ul>
      </p>
    </form>
  );
}
